import Enumerable from "linq";
import { IDocumentEditable } from "../IDocumentEditable";
import {
  IItem,
  InjectedRule,
  instanceOfIHisInformationApplicable,
  instanceOfINameSpecifiable,
  instanceOfIRuleInjectable,
  instanceOfISubNameSpecifiable,
  IRuleInjectable,
} from "../../../../domain/document/items";
import { User } from "../../../../domain/user";
import { EditRuleBinding, EditRuleCondition, EditRuleSet } from "../../../../domain/document";
import { LoggerFactory } from "../../../../utilities/logger";
import { ProjectTeam } from "../../../../domain/projectTeam";
import { DomainRegistry } from "../../../../domain";

export class EditRuleSetApplicator {
  constructor(private _document: IDocumentEditable, private _user: User) {
  }
  private _projectTeams: ProjectTeam[] = [];

  async apply(): Promise<void> {
    const editRuleSet = this._document.source.attributes.editRuleSet;
    if (!editRuleSet) { return; }

    LoggerFactory.getLogger().info("制約の注入を開始します。");
    await this.fillProjectTeamCache(editRuleSet);
    const itemSetId = 0;  // 続紙は非対応とする
    const itemSet = this._document.itemSets[itemSetId];
    for (const item of itemSet.items) {
      if (!instanceOfIRuleInjectable(item)) { continue; }
      const condition = this.getEditRuleCondition(item);
      if (!condition || (!condition.uneditable && !condition.emptyCheck)) { continue; }

      const rule = new InjectedRule();
      rule.isEditDisabled = !!condition.uneditable;
      rule.isNameGroupEmptyCheckEnabled = !!condition.emptyCheck;

      const cloned = item.clone() as IRuleInjectable;
      cloned.injectedRule = rule;

      this._document.updateItem(cloned, {
        itemSetId,
        isGeometryOnly: true, // 連鎖更新は不要なのでフラグを流用している
      });
    }
    LoggerFactory.getLogger().info("制約の注入が完了しました。");
  }

  private async fillProjectTeamCache(editRuleSet: EditRuleSet): Promise<void> {
    if (Enumerable.from(editRuleSet.patterns).selectMany(p => p.conditions).all(c => c.projectTeamId == null)) {
      // プロジェクトチームの指定が無い場合は取得不要
      return;
    }
    // 削除済みも取得している
    const repo = DomainRegistry.projectTeam.getProjectTeamRepository();
    this._projectTeams = await repo.getAllEntities();
  }

  getEditRuleCondition(item: IItem): EditRuleCondition {
    // 並び順に従って先頭から判定する
    for (const pattern of this._document.source.attributes.editRuleSet.patterns) {
      const isMatched = pattern.bindings.some(b => this.isBindingMatched(item, b));
      if (!isMatched) { continue; }

      // 複数の条件を紐づける挙動はさせない見込み（収拾がつかなくなる）
      return Enumerable.from(pattern.conditions).firstOrDefault(c => this.isUserConditionMatched(c));
    }
    return null;
  }

  private isBindingMatched(item: IItem, binding: EditRuleBinding): boolean {
    if (!binding.objectName && !binding.objectSubName && !binding.objectTitle) { return false; }
    if (binding.objectName) {
      if (instanceOfIHisInformationApplicable(item) && item.hisItemParameter) {
        if (item.hisItemParameter.key === binding.objectName) {
          // 一致
        } else {
          return false;
        }
      } else {
        // フリー入力オブジェクト・日付オブジェクト・時刻オブジェクト以外はtitle属性に分かれずname属性に入っている
        if (instanceOfINameSpecifiable(item) && item.name === binding.objectName) {
          // 一致
        } else {
          return false;
        }
      }
    }
    if (binding.objectSubName) {
      if (instanceOfISubNameSpecifiable(item) && item.subName === binding.objectSubName) {
        // 一致
      } else {
        return false;
      }
    } else {
      if (!instanceOfISubNameSpecifiable(item) || !item.subName) {
        // 一致
      } else {
        return false;
      }
    }
    if (binding.objectTitle) {
      // HIS項目であればtitleではなくnameとname-subでの指定になっているはずなので除外している
      if (instanceOfINameSpecifiable(item) && item.name === binding.objectTitle && !(instanceOfIHisInformationApplicable(item) && item.isHisInfo)) {
        // 一致
      } else {
        return false;
      }
    }
    return true;
  }

  private isUserConditionMatched(condition: EditRuleCondition): boolean {
    const userProfessionCode = this._user.profession ? this._user.profession.id : null;
    if (condition.professionCode) {
      if (condition.professionCode !== userProfessionCode) { return false; }
    }
    if (condition.projectTeamId != null) {
      const found = Enumerable.from(this._projectTeams).firstOrDefault(p => p.id === condition.projectTeamId);
      if (found) {
        return Enumerable.from(found.members).any(m => m.id.toUpperCase() === this._user.id.toUpperCase());
      }
      // TODO: Errorをthrowしたほうがよい？
      LoggerFactory.getLogger().error("制約に指定されたプロジェクトチームが見つかりませんでした。id=" + condition.projectTeamId);
      return false;
    }
    return true;
  }
}
